import { useEffect, useRef, useState } from 'react';
import PortalLayout from './PortalLayout';
import portalClient from './portalClient';
import { createChatSocket, emitAck } from '../lib/chatSocket';
import ChatMessageBubble from '../components/chat/ChatMessageBubble';
import { ACCEPT, checkFile, summarise } from '../lib/chatFiles';
import useScrollBackLoader from '../components/chat/useScrollBackLoader';
import { Button } from '../design-system';
import './Portal.css';
import '../org-admin/styles/Chat.css';

// The client's side of the conversation.
//
// Note what this component never does: it never names a client. The socket
// handshake and every REST call are scoped server-side to the ClientUser's own
// record, so there is no client id to send and nothing here to get wrong.

const fmtDay = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(date, today)) return 'Today';
  if (same(date, yest)) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function PortalChat() {
  const [messages, setMessages] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [responseNote, setResponseNote] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [firmTyping, setFirmTyping] = useState(null);
  const [error, setError] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);

  const socketRef = useRef(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  // Older messages load as the reader scrolls up; the hook preserves their
  // reading position across the prepend.
  const { scrollRef, onScroll } = useScrollBackLoader({ hasMore, loadOlder: () => loadOlder() });
  const typingTimer = useRef(null);

  useEffect(() => {
    portalClient
      .get('/api/portal/chat')
      .then((r) => {
        setEnabled(r.data.enabled !== false);
        setMessages(r.data.messages || []);
        setHasMore(!!r.data.hasMore);
        setResponseNote(r.data.responseNote || '');
      })
      .catch((e) => setError(e.response?.data?.error || 'Could not load your messages'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const socket = createChatSocket('client');
    socketRef.current = socket;
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));
    socket.on('chat:message', (msg) => {
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      // Anything arriving while this screen is open has been seen.
      if (!msg.fromClient) emitAck(socket, 'chat:read', {});
    });
    socket.on('chat:typing', (evt) => {
      setFirmTyping(evt.isTyping ? evt.who : null);
      if (evt.isTyping) {
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setFirmTyping(null), 4000);
      }
    });
    return () => {
      clearTimeout(typingTimer.current);
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, []);

  // Follow the conversation down only when something was APPENDED (a new
  // message arrived, or the thread changed) — never on a PREPEND from scrolling
  // up, which would yank the reader straight back to the bottom and make
  // loading history impossible.
  const lastIdRef = useRef(null);
  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id || null;
    if (lastId !== lastIdRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
      lastIdRef.current = lastId;
    }
  }, [messages, loading]);

  const loadOlder = async () => {
    const oldest = messages[0];
    if (!oldest) return;
    try {
      const r = await portalClient.get('/api/portal/chat', { params: { before: oldest.sentAt } });
      setMessages((prev) => [...(r.data.messages || []), ...prev]);
      setHasMore(!!r.data.hasMore);
    } catch {
      setError('Could not load older messages');
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      const socket = socketRef.current;
      // Socket first, HTTP as the fallback — a client on a network that blocks
      // websockets must still be able to reach their accountant.
      const replyToId = replyTo?.id || null;
      const res = socket?.connected ? await emitAck(socket, 'chat:send', { body, replyToId }) : { error: 'offline' };
      if (res.ok) {
        setDraft('');
        setReplyTo(null);
      } else {
        const r = await portalClient.post('/api/portal/chat/messages', { body, replyToId });
        setDraft('');
        setReplyTo(null);
        setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Your message could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const sendFile = async (file) => {
    const problem = checkFile(file);
    if (problem) { setError(problem); return; }
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      if (draft.trim()) form.append('body', draft.trim());
      if (replyTo?.id) form.append('replyToId', replyTo.id);
      const r = await portalClient.post('/api/portal/chat/attachments', form);
      setDraft('');
      setReplyTo(null);
      setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
    } catch (err) {
      setError(err.response?.data?.error || 'That file could not be sent');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // A file sent in chat is created with sharedWithClient: true, which is exactly
  // what the existing portal document route scopes on — so there is no separate
  // chat-file endpoint on this side to keep in step.
  const openAttachment = async (file) => {
    try {
      const r = await portalClient.get(`/api/portal/documents/${file.documentId}/url`);
      return r.data.source === 'stream'
        ? `${portalClient.defaults.baseURL || ''}${r.data.url}`
        : r.data.url;
    } catch {
      setError('Could not open that file');
      return null;
    }
  };

  const jumpToReply = (id) => {
    const el = document.getElementById(`chat-msg-${id}`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('chat-msg-flash');
      setTimeout(() => el.classList.remove('chat-msg-flash'), 1200);
    }
  };

  const onDraft = (v) => {
    setDraft(v);
    const socket = socketRef.current;
    if (socket?.connected) socket.emit('chat:typing', { isTyping: v.length > 0 });
  };

  const rows = [];
  let lastDay = null;
  messages.forEach((m) => {
    const day = fmtDay(m.sentAt);
    if (day !== lastDay) { rows.push({ kind: 'day', id: `d-${day}-${m.id}`, day }); lastDay = day; }
    rows.push({ kind: 'msg', ...m });
  });

  if (!enabled) {
    return (
      <PortalLayout title="Messages" subtitle="">
        <div className="chat-portal-card chat-portal-off">
          <p>Messaging isn't switched on for your account right now.</p>
          <p className="portal-muted">You can still raise a request, and your accountant will reply there.</p>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Messages" subtitle={responseNote || 'Chat directly with your accountant'}>
      <div className="chat-portal-card chat-portal">
        <div className="chat-portal-status">
          <span className={`chat-conn chat-conn-${connected ? 'on' : 'off'}`}>
            <span className="chat-conn-dot" />
            {connected ? 'Connected' : 'Reconnecting…'}
          </span>
        </div>

        <div className="chat-scroll chat-portal-scroll" ref={scrollRef} onScroll={onScroll}>
          {loading ? (
            <p className="chat-empty-note chat-empty-center">Loading your messages…</p>
          ) : (
            <>
              {hasMore && (
                <div className="chat-older">
                  <Button variant="secondary" size="sm" onClick={loadOlder}>Load older messages</Button>
                </div>
              )}
              {rows.length === 0 && (
                <p className="chat-empty-note chat-empty-center">
                  No messages yet. Ask us anything — about a filing, a document, or a deadline.
                </p>
              )}
              {rows.map((r) =>
                r.kind === 'day' ? (
                  <div className="chat-day" key={r.id}><span>{r.day}</span></div>
                ) : (
                  <div key={r.id} id={`chat-msg-${r.id}`}>
                    <ChatMessageBubble
                      message={r}
                      mine={r.fromClient}
                      showSender={!r.fromClient}
                      onReply={setReplyTo}
                      onOpenAttachment={openAttachment}
                      onJumpToReply={jumpToReply}
                    />
                  </div>
                )
              )}
              {firmTyping && <div className="chat-typing">{firmTyping} is typing…</div>}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {error && <p className="chat-error" role="alert">{error}</p>}

        {replyTo && (
          <div className="chat-reply-strip">
            <div className="chat-reply-strip-main">
              <span className="chat-reply-strip-who">Replying to {replyTo.senderName}</span>
              <span className="chat-reply-strip-text">{summarise(replyTo)}</span>
            </div>
            <button type="button" className="chat-reply-cancel" onClick={() => setReplyTo(null)} aria-label="Cancel reply">✕</button>
          </div>
        )}

        <form className="chat-composer" onSubmit={send}>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="chat-file-input"
            onChange={(e) => e.target.files?.[0] && sendFile(e.target.files[0])}
          />
          <button
            type="button"
            className="chat-attach-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Attach a file"
            aria-label="Attach a file"
          >
            {uploading ? '…' : '📎'}
          </button>
          <textarea
            className="chat-input"
            placeholder="Write a message…"
            value={draft}
            rows={1}
            onChange={(e) => onDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
          />
          <Button type="submit" variant="primary" disabled={!draft.trim() || sending} loading={sending}>
            Send
          </Button>
        </form>
      </div>
    </PortalLayout>
  );
}

export default PortalChat;
